import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { LogOut, History, User, Settings as SettingsIcon } from "lucide-react";
import { UploadPhoto } from "@/components/dashboard/UploadPhoto";
import { QuoteEditor } from "@/components/dashboard/QuoteEditor";
import { InvoiceActions } from "@/components/dashboard/InvoiceActions";
import { HowToUse } from "@/components/dashboard/HowToUse";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTrialAccess } from "@/hooks/useTrialAccess";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import logo from "@/assets/logo.png";

const Dashboard = () => {
  const navigate = useNavigate();
  const [photoUrl, setPhotoUrl] = useState("");
  const [materials, setMaterials] = useState<any[]>([]);
  const [laborCost, setLaborCost] = useState(0);
  const [showEditor, setShowEditor] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { trialUsesLeft, hasTrialAccess, consumeTrialUse, resetTrialUses, maxTrialUses } = useTrialAccess();

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
    setIsAuthenticated(!!user);
    
    // Reset trial uses for authenticated users
    if (user) {
      resetTrialUses();
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const handleAnalysisComplete = (data: any) => {
    // Consume a trial use for anonymous users
    if (!isAuthenticated) {
      const remaining = consumeTrialUse();
      if (remaining === 0) {
        // Show alert that they need to sign up
        return;
      }
    }
    
    setMaterials(data.materials || []);
    setLaborCost(data.labor_cost || 0);
    setShowEditor(true);
  };

  const handleQuoteUpdate = (updatedMaterials: any[], updatedLabor: number) => {
    setMaterials(updatedMaterials);
    setLaborCost(updatedLabor);
  };

  const total = materials.reduce((sum, m) => sum + (m.quantity * m.estimated_cost), 0) + laborCost;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2 md:gap-3">
            <img src={logo} alt="SnapBill" className="h-8 md:h-10 w-8 md:w-10" />
            <div className="hidden sm:block">
              <h1 className="font-display font-bold text-lg md:text-xl">SnapBill</h1>
              <p className="text-xs text-muted-foreground">Snap. Bill. Paid.</p>
            </div>
          </div>
          <div className="flex gap-1 md:gap-2">
            <ThemeToggle />
            {isAuthenticated ? (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate("/settings")} className="hidden md:flex">
                  <SettingsIcon className="h-4 w-4 mr-2" />
                  Settings
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate("/settings")} className="md:hidden">
                  <SettingsIcon className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate("/history")} className="hidden md:flex">
                  <History className="h-4 w-4 mr-2" />
                  History
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate("/history")} className="md:hidden">
                  <History className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleSignOut} className="hidden md:flex">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </Button>
                <Button variant="ghost" size="sm" onClick={handleSignOut} className="md:hidden">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="default" size="sm" onClick={() => navigate("/")} className="hidden sm:flex">
                  <User className="h-4 w-4 mr-2" />
                  Sign in
                </Button>
                <Button variant="default" size="sm" onClick={() => navigate("/")} className="sm:hidden">
                  <User className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="container py-4 md:py-8 px-4">
        <div className="max-w-4xl mx-auto space-y-6 md:space-y-8 mb-20 md:mb-8">
          {!isAuthenticated && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-4 md:pt-6">
                <Alert>
                  <User className="h-4 w-4" />
                  <AlertTitle className="text-sm md:text-base">Free Trial Mode</AlertTitle>
                  <AlertDescription className="text-xs md:text-sm">
                    You have <span className="font-bold text-primary">{trialUsesLeft} of {maxTrialUses}</span> free uses remaining.
                    {trialUsesLeft <= 2 && (
                      <span className="block mt-2">
                        Sign up now to get <span className="font-semibold">unlimited access</span> and save your invoices!
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          )}
          
          {!hasTrialAccess && !isAuthenticated && (
            <Card className="border-destructive bg-destructive/10">
              <CardContent className="pt-4 md:pt-6">
                <Alert variant="destructive">
                  <AlertTitle className="text-sm md:text-base">Trial Limit Reached</AlertTitle>
                  <AlertDescription className="text-xs md:text-sm">
                    You've used all {maxTrialUses} free tries. Sign up now to continue creating invoices!
                    <div className="mt-4">
                      <Button onClick={() => navigate("/")} size="sm">
                        Sign Up for Free
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          )}
          
          <div>
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Create New Invoice</h2>
            <p className="text-sm md:text-base text-muted-foreground">
              {isAuthenticated 
                ? "Upload a photo to get started with AI-powered quotes" 
                : "Try our AI-powered quote generator - no account needed!"}
            </p>
          </div>

          {!showEditor && (
            <HowToUse />
          )}

          {(hasTrialAccess || isAuthenticated) && (
            <UploadPhoto
              onPhotoUploaded={setPhotoUrl}
              onAnalysisComplete={handleAnalysisComplete}
            />
          )}

          {showEditor && (
            <>
              <QuoteEditor
                materials={materials}
                laborCost={laborCost}
                onUpdate={handleQuoteUpdate}
              />

              <InvoiceActions
                materials={materials}
                laborCost={laborCost}
                total={total}
                photoUrl={photoUrl}
                isAuthenticated={isAuthenticated}
                onSignUpRequired={() => navigate("/")}
              />
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
